import filtersModel from '@/mixins/filters-model';
import cardsModel from '@/mixins/cards-model';
import channelModel from '@/mixins/channel-model';
import pagesModel from '@/mixins/pages-model';
import sacTypeModel from '@/mixins/sac-type-model';
import WarRoomService from '@/services/WarRoomService';
import { mapActions, mapGetters } from 'vuex';
import { POLARITY_TYPES } from '@/utils/constants';

export default {
  mixins: [
    filtersModel,
    cardsModel,
    pagesModel,
    sacTypeModel,
    channelModel,
  ],
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('filter', [
      'filterByDataType',
    ]),
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    selectedUsersFilter() {
      return this.card.props.filter && this.card.props.filter.uids ? this.card.props.filter.uids.value : null;
    },
    loversAndHatersFilter() {
      return this.card.props.filter && this.card.props.filter?.lovers_and_haters ? this.card.props.filter?.lovers_and_haters?.value : null;
    },
    selectedPagesFilter() {
      return this.card.props.filter && this.card.props.filter?.pages ? this.card.props.filter?.pages?.value : null;
    },
    loversAndHatersFilterLength() {
      return this.loversAndHatersFilter?.length;
    },
    selectedPagesFilterLength() {
      return this.selectedPagesFilter?.length;
    },
  },
  watch: {
    options: {
      handler() {
        this.getData();
      },
      deep: true,
    },
  },
  created() {
    this.card.props.filter = {};
  },
  mounted() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getPerformanceService',
      'getWarroomService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    openDialog() {
      this.dialog = true;
    },
    getPageName(pageId) {
      return this.pagesFormatted.find(({ value }) => value === pageId)?.text || '';
    },
    getChannelForPage(pageId) {
      return this.pagesFormatted.find(({ value }) => value === pageId)?.channel || '';
    },
    async aggregateResponses(responses) {
      const usersList = [];

      responses.map((response) => {
        if (response.hasOwnProperty('data')) {
          const pageName = this.getPageName(response.request_parameters.page_id);
          response.data.users.map((user) => {
            user.page = pageName;
            user.pageId = response.request_parameters.page_id;
            usersList.push(user);
            return user;
          });
        }
        return response;
      });

      const orderedList = this.orderUserListByPolarity(usersList);

      const filteredListByPolarity = this.evaluatePolarityConditionAndFilter(orderedList);

      const filteredListByPolarityAndUids = this.filterUsersByUid(filteredListByPolarity);

      const filterId = await this.encodeFilter();
      const users = await this.getUserPost(filterId, filteredListByPolarityAndUids);

      return users;
    },
    getPromisses() {
      const promisses = [];
      if (this.card.props?.filter && this.card.props?.filter?.pages?.value.length > 0) {
        for (let i = 0; i < this.card.props.filter.pages.value.length; i++) {
          const page = this.card.props.filter.pages.value[i];
          const channel = this.getChannelForPage(page);
          promisses.push(this.createRequisitions(channel, page));
        }

        return promisses;
      }

      for (let i = 0; i < this.pagesFormatted.length; i++) {
        const channel = this.pagesFormatted[i].channel;
        const page = this.pagesFormatted[i].value;
        promisses.push(this.createRequisitions(channel, page));
      }
      return promisses;
    },
    createRequisitions(channel, page) {
      return this.getPerformanceService({
        itemId: this.itemId,
        servicePath: 'engagedUsers',
        serviceParams: {
          page_id: page,
          channel,
          limit: 100,
          offset: 1,
        },
      });
    },
    async getUserPost(filterId, userList) {
      let allUserPosts = [];
      let offset = 0;
      const limit = 1000;

      try {
        let hasMoreData = true;
        while (hasMoreData) {
          const response = await this.getPerformanceService({
            itemId: this.itemId,
            servicePath: 'userPosts',
            serviceParams: {
              filterId,
              limit,
              offset,
              projection: 'uid,polarity,name',
            },
          });

          const usersPosts = response.data?.posts || [];

          if (usersPosts.length > 0) {
            allUserPosts = allUserPosts.concat(usersPosts);
            offset += limit;
          } else {
            hasMoreData = false;
          }
        }

        const users = userList.map((user) => {
          const userPosts = allUserPosts.filter((post) => {
            return user.uid === post.uid && post.pages.includes(user.pageId);
          });

          const positiveCount = userPosts.filter((post) => post.polarity === POLARITY_TYPES.POSITIVE).length;
          const negativeCount = userPosts.filter((post) => post.polarity === POLARITY_TYPES.NEGATIVE).length;
          const neutralCount = userPosts.filter((post) => post.polarity === POLARITY_TYPES.NEUTRAL).length;
          const notSentimentCount = userPosts.filter((post) => post.polarity === POLARITY_TYPES.NO_POLARITY).length;

          const totalPosts = positiveCount + negativeCount + neutralCount + notSentimentCount;

          const polarity = ((positiveCount + neutralCount + notSentimentCount) / totalPosts) * 100;
          user.polarity = polarity.toFixed(2);

          return user;
        });

        return users;
      } catch (error) {
        console.error(error);
        this.requestStatus = 'error';
      }
    },
    orderUserListByPolarity(userList) {
      return [...userList].sort((a, b) => b.polarity - a.polarity);
    },
    evaluatePolarityConditionAndFilter(orderedUsersList) {
      const userListWithPolarityApplied = this.applyPolarityFilter(orderedUsersList, this.loversAndHatersFilter);

      this.setUsersFilterProp(userListWithPolarityApplied);

      return userListWithPolarityApplied;
    },
    applyPolarityFilter(userList, filter) {
      if (this.loversAndHatersFilterLength < 2) {
        return this.selectedPagesFilterLength === 1
          ? this.applyPolarityFilterToUserList(filter, userList)
          : this.applyPolarityFilterToUserListWithMoreThanOnePageSelected(filter, userList);
      }

      return userList;
    },
    applyPolarityFilterToUserList(filter, users) {
      if (filter?.includes(this.loversString)) {
        return users.slice(0, 50);
      }

      return users.slice(50);
    },
    applyPolarityFilterToUserListWithMoreThanOnePageSelected(filter, users) {
      if (filter?.includes(this.loversString)) {
        return users.slice(0, Math.ceil(users.length / 2));
      }

      return users.slice(Math.ceil(users.length / 2));
    },
    filterUsersByUid(users) {
      const filter = this.selectedUsersFilter;
      if (filter !== null) {
        return users.filter(user => filter.includes(user.uid));
      }
      return users;
    },
    async encodeFilter() {
      const pages = this.card.props.filter && this.card.props.filter.pages?.value ? this.card.props.filter.pages?.value : this.pagesFormatted.map(page => page.value);
      const newCardFilter = {
        pages: {
          value: pages,
        },
      };
      return WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });
    },
    setUsersFilterProp(users) {
      if (this.card.props.filter) {
        this.card.props.filter.users = { value: users };
      }
    },
  },
};
